import { clearPlanningCache } from './planningCache';
import { environment } from '../config/environment';

export const AUTH_SESSION_REFRESH_FAILED = 'congplanner:auth-session-refresh-failed';
export const AUTH_SESSION_CHANGED = 'congplanner:auth-session-changed';
export class SessionExpiredError extends Error {}
export class SessionChangedError extends Error {}

export const clearAuthSession = (): void => {
  clearPlanningCache();
  for (const key of ['auth_token', 'id_token', 'refresh_token', 'user_data']) {
    localStorage.removeItem(key);
  }
  window.dispatchEvent(new Event(AUTH_SESSION_CHANGED));
};

let pendingRefresh: Promise<string> | null = null;

// Share one request between startup, timers and concurrent API retries.
export const refreshAuthSession = (): Promise<string> => {
  if (pendingRefresh) return pendingRefresh;
  pendingRefresh = performRefresh().finally(() => {
    pendingRefresh = null;
  });
  return pendingRefresh;
};

const performRefresh = async (): Promise<string> => {
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) {
    clearAuthSession();
    throw new SessionExpiredError('No refresh token available');
  }
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 15000);
  try {
    const response = await fetch(`${environment.apiUrl.replace(/\/+$/, '')}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
      signal: controller.signal,
    });
    // A response from an earlier session must never undo logout or a new login.
    if (localStorage.getItem('refresh_token') !== refreshToken) {
      throw new SessionChangedError('Session changed during refresh');
    }
    if (response.status === 401) {
      clearAuthSession();
      throw new SessionExpiredError('Session expired. Please log in again.');
    }
    if (!response.ok) throw new Error('Session refresh temporarily unavailable');
    const rawData = await response.json();
    const data = rawData?.data ?? rawData;
    const token = data?.accessToken || data?.token;
    if (
      typeof token !== 'string' ||
      !token ||
      typeof data?.idToken !== 'string' ||
      !data.idToken ||
      (data.refreshToken !== undefined &&
        (typeof data.refreshToken !== 'string' || !data.refreshToken))
    ) {
      throw new Error('Invalid refresh response');
    }
    if (localStorage.getItem('refresh_token') !== refreshToken) {
      throw new SessionChangedError('Session changed during refresh');
    }
    localStorage.setItem('auth_token', token);
    if (data.idToken) localStorage.setItem('id_token', data.idToken);
    if (data.refreshToken) localStorage.setItem('refresh_token', data.refreshToken);
    window.dispatchEvent(new Event(AUTH_SESSION_CHANGED));
    return token;
  } catch (error) {
    if (
      !(error instanceof SessionExpiredError) &&
      !(error instanceof SessionChangedError) &&
      localStorage.getItem('refresh_token') === refreshToken
    ) {
      window.dispatchEvent(new Event(AUTH_SESSION_REFRESH_FAILED));
    }
    throw error;
  } finally {
    window.clearTimeout(timeout);
  }
};
